import React, { Fragment, useState, useEffect, useCallback } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Table,
  FormGroup,
  Label,
  Input,
  CustomInput,
  Button,
  Row,
  Col,
  Badge,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Alert,
  Form
} from "reactstrap";
import {
  Bell,
  Eye,
  ArrowRight,
  Calendar,
  AlertCircle,
  RefreshCw,
  RefreshCcw,
  Search,
  Send,
  Trash2,
  Edit2,
  Plus,
  ExternalLink,
  Clock,
  CheckCircle,
  XCircle,
  Layers,
  MoreVertical,
  MoreHorizontal
} from "react-feather";
import DataTable from "react-data-table-component";
import Flatpickr from "react-flatpickr";
import "@styles/react/libs/flatpickr/flatpickr.scss";
import { successAlert, confirmAlert, errorAlert } from "@src/views/components/CustomAlerts";
import CustomPagination from "@src/views/components/pagination/CustomPagination";
import "@src/views/brands/dashboard/dashboardGuidance.scss";
import "./announcements.scss";

import axios from "axios";
import { API_URL } from "@src/redux/actionTypes";

const getToken = () => localStorage.getItem(process.env.REACT_APP_USERTOKEN);

export const VALID_INTERNAL_ACTIONS = [
  { value: "create_post", label: "Outreach: Create Campaign" },
  { value: "view_programs", label: "Programs: View Outreach Programs" },
  { value: "open_messages", label: "Messages: Open Chat" },
  { value: "browse_rds", label: "Directory: Browse Dietitians" }
];

export const DEFAULT_ANNOUNCEMENT = {
  id: null,
  title: "",
  body: "",
  is_active: false,
  starts_at: null,
  ends_at: null,
  cta_enabled: false,
  cta: {
    label: "",
    type: "internal",
    action: "create_post",
    url: ""
  }
};

export const cleanIsoDate = (val) => {
  if (!val) return null;
  if (typeof val === "string" && !val.trim()) return null;
  try {
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d.toISOString();
  } catch (e) {
    return null;
  }
};

export const formatDateDisplay = (isoStr) => {
  if (!isoStr) return "-";
  try {
    const d = new Date(isoStr);
    if (isNaN(d.getTime())) return "-";
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch (e) {
    return "-";
  }
};

export const validateAnnouncementForm = (formData) => {
  const errs = {};

  if (!formData || !formData.title || !formData.title.trim()) {
    errs.title = "Announcement title is required.";
  } else if (formData.title.trim().length > 191) {
    errs.title = "Title must be 191 characters or less.";
  }

  if (!formData || !formData.body || !formData.body.trim()) {
    errs.body = "Announcement body is required.";
  } else if (formData.body.trim().length > 5000) {
    errs.body = "Body must be 5000 characters or less.";
  }

  if (formData && formData.cta_enabled) {
    if (!formData.cta?.label || !formData.cta.label.trim()) {
      errs.cta_label = "CTA button label is required when CTA is enabled.";
    } else if (formData.cta.label.trim().length > 80) {
      errs.cta_label = "CTA label must be 80 characters or less.";
    }

    if (formData.cta?.type === "external") {
      const url = formData.cta?.url?.trim() || "";
      if (!url) {
        errs.cta_url = "An HTTPS URL is required for an external CTA.";
      } else if (!url.startsWith("https://")) {
        errs.cta_url = "An HTTPS URL is required (must start with https://).";
      } else if (url.length > 500) {
        errs.cta_url = "CTA URL must be 500 characters or less.";
      }
    }
  }

  const startIso = cleanIsoDate(formData?.starts_at);
  const endIso = cleanIsoDate(formData?.ends_at);
  if (startIso && endIso) {
    if (new Date(endIso).getTime() <= new Date(startIso).getTime()) {
      errs.ends_at = "The end time must be after the start time.";
    }
  }

  return errs;
};

export const getScheduleStatus = (item, now = Date.now()) => {
  if (!item) return "inactive";
  const start = item.starts_at ? new Date(item.starts_at).getTime() : null;
  const end = item.ends_at ? new Date(item.ends_at).getTime() : null;

  if (end && now > end) return "expired";
  if (start && now < start) return "scheduled";
  if (item.is_active) return "live";
  return "inactive";
};

export const getScheduleBadge = (item, now = Date.now()) => {
  const status = getScheduleStatus(item, now);
  if (status === "expired") {
    return <Badge color="light-danger">Expired</Badge>;
  }
  if (status === "scheduled") {
    return <Badge color="light-warning">Scheduled</Badge>;
  }
  if (status === "live") {
    return (
      <Badge color="light-success" className="d-inline-flex align-items-center">
        <span className="live-pulse-dot" /> Live Now
      </Badge>
    );
  }
  return <Badge color="light-secondary">Inactive</Badge>;
};

export const paginateList = (items = [], page = 1, pageSize = 10) => {
  const list = Array.isArray(items) ? items : [];
  const validPageSize = Math.max(1, parseInt(pageSize, 10) || 10);
  const total = list.length;
  const totalPages = Math.max(1, Math.ceil(total / validPageSize));
  const safePage = Math.min(Math.max(1, parseInt(page, 10) || 1), totalPages);
  const startIndex = (safePage - 1) * validPageSize;
  const endIndex = Math.min(startIndex + validPageSize, total);

  return {
    items: list.slice(startIndex, endIndex),
    currentPage: safePage,
    totalPages,
    total,
    startIndex,
    endIndex,
    pageSize: validPageSize
  };
};

export const generatePaginationLinks = (currentPage = 1, totalPages = 1) => {
  const current = Math.max(1, parseInt(currentPage, 10) || 1);
  const total = Math.max(1, parseInt(totalPages, 10) || 1);
  const links = [];

  links.push({
    label: "Previous",
    url: current > 1 ? String(current - 1) : null,
    active: false
  });

  if (total <= 5) {
    for (let i = 1; i <= total; i++) {
      links.push({
        label: String(i),
        url: String(i),
        active: i === current
      });
    }
  } else {
    if (current <= 3) {
      for (let i = 1; i <= 3; i++) {
        links.push({
          label: String(i),
          url: String(i),
          active: i === current
        });
      }
      links.push({ label: "...", url: null, active: false });
      links.push({ label: String(total), url: String(total), active: false });
    } else if (current >= total - 2) {
      links.push({ label: "1", url: "1", active: false });
      links.push({ label: "...", url: null, active: false });
      for (let i = total - 2; i <= total; i++) {
        links.push({
          label: String(i),
          url: String(i),
          active: i === current
        });
      }
    } else {
      links.push({ label: "1", url: "1", active: false });
      links.push({ label: "...", url: null, active: false });
      for (let i = current - 1; i <= current + 1; i++) {
        links.push({
          label: String(i),
          url: String(i),
          active: i === current
        });
      }
      links.push({ label: "...", url: null, active: false });
      links.push({ label: String(total), url: String(total), active: false });
    }
  }

  links.push({
    label: "Next",
    url: current < total ? String(current + 1) : null,
    active: false
  });

  return links;
};

export const getInitialPaginationState = (
  searchStr = typeof window !== "undefined" ? window.location?.search : "",
  storage = typeof window !== "undefined" ? window.sessionStorage : null
) => {
  let page = 1;
  let perPage = 10;

  try {
    if (searchStr) {
      const params = new URLSearchParams(searchStr);
      const urlPage = parseInt(params.get("page"), 10);
      const urlPerPage = parseInt(params.get("per_page"), 10);
      if (!isNaN(urlPage) && urlPage > 0) page = urlPage;
      if (!isNaN(urlPerPage) && [5, 10, 20, 50].includes(urlPerPage)) perPage = urlPerPage;
    } else if (storage) {
      const savedPage = parseInt(storage.getItem("rdlink_admin_announcements_page"), 10);
      const savedPerPage = parseInt(storage.getItem("rdlink_admin_announcements_per_page"), 10);
      if (!isNaN(savedPage) && savedPage > 0) page = savedPage;
      if (!isNaN(savedPerPage) && [5, 10, 20, 50].includes(savedPerPage)) perPage = savedPerPage;
    }
  } catch (e) {}

  return { page, perPage };
};

export const syncPaginationToStorageAndUrl = (page, perPage) => {
  try {
    if (typeof window !== "undefined") {
      if (window.sessionStorage) {
        window.sessionStorage.setItem("rdlink_admin_announcements_page", String(page));
        window.sessionStorage.setItem("rdlink_admin_announcements_per_page", String(perPage));
      }
      if (window.location && window.history?.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set("page", String(page));
        url.searchParams.set("per_page", String(perPage));
        window.history.replaceState(null, "", `${url.pathname}${url.search}`);
      }
    }
  } catch (e) {}
};

const AdminAnnouncements = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    total: 0,
    perPage: 20
  });

  const initialPagination = getInitialPaginationState();
  const [currentPage, setCurrentPage] = useState(initialPagination.page);
  const [pageSize, setPageSize] = useState(initialPagination.perPage);

  const [formData, setFormData] = useState(DEFAULT_ANNOUNCEMENT);
  const [errors, setErrors] = useState({});
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isTogglingId, setIsTogglingId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Search & Filter state (inputs vs applied filter on click)
  const [searchTitle, setSearchTitle] = useState("");
  const [searchStatus, setSearchStatus] = useState("");
  const [appliedTitle, setAppliedTitle] = useState("");
  const [appliedStatus, setAppliedStatus] = useState("");

  const isFilterActive = Boolean(appliedTitle || appliedStatus || searchTitle.trim() || searchStatus);

  // Delete confirmation modal state (matching manage-brands)
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // Alert message state (matching manage-brands)
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [alertMsg, setAlertMsg] = useState("");
  const [alertType, setAlertType] = useState("success");

  const showAlert = (msg, type = "success") => {
    setAlertMsg(msg);
    setAlertType(type);
    setVisibleAlert(true);
    setTimeout(() => {
      setVisibleAlert(false);
      setAlertMsg("");
    }, 4000);
  };

  // ─── Fetch List of Announcements (Backend API: GET /api/admin/brand-announcements?page=X) ───
  const fetchAnnouncements = useCallback((page = 1) => {
    setIsLoadingList(true);
    const token = getToken();

    axios
      .get(`${API_URL}admin/brand-announcements?page=${page}`, {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`
        }
      })
      .then((res) => {
        const paginator = res?.data?.data || {};
        const list = Array.isArray(paginator.data)
          ? paginator.data
          : Array.isArray(paginator)
            ? paginator
            : [];

        setAnnouncements(list);
        setPagination({
          currentPage: paginator.current_page || page,
          lastPage: paginator.last_page || 1,
          total: paginator.total !== undefined ? paginator.total : list.length,
          perPage: paginator.per_page || 20
        });

        // Sync active announcement to localStorage
        const activeItem = list.find((a) => Boolean(a.is_active));
        if (activeItem) {
          localStorage.setItem("rdlink_admin_announcement", JSON.stringify(activeItem));
        }
      })
      .catch((err) => {
        console.error("Failed to load announcements list:", err);
      })
      .finally(() => {
        setIsLoadingList(false);
      });
  }, []);

  // Initial load respecting persisted page from URL / sessionStorage
  useEffect(() => {
    const init = getInitialPaginationState();
    fetchAnnouncements(init.page);
  }, [fetchAnnouncements]);

  // Handle page click from reusable CustomPagination
  const handleCustomPagination = (pageUrl) => {
    if (!pageUrl) return;
    let pageNo = pageUrl;
    if (typeof pageUrl === "string" && pageUrl.includes("page=")) {
      const match = pageUrl.match(/page=(\d+)/);
      if (match) pageNo = match[1];
    }
    const targetPage = parseInt(pageNo, 10);
    if (!isNaN(targetPage) && targetPage > 0) {
      setCurrentPage(targetPage);
      syncPaginationToStorageAndUrl(targetPage, pageSize);
      if (pagination.lastPage > 1 && targetPage !== pagination.currentPage) {
        fetchAnnouncements(targetPage);
      }
    }
  };

  // Load a specific announcement into the Editor modal
  const handleEditAnnouncement = (item) => {
    const isExt = item.cta_action === "external_url";
    const rawAction = item.cta_action;
    const cleanAction =
      !isExt && VALID_INTERNAL_ACTIONS.some((a) => a.value === rawAction)
        ? rawAction
        : "create_post";

    setFormData({
      id: item.id,
      title: item.title || "",
      body: item.body || "",
      is_active: Boolean(item.is_active),
      starts_at: item.starts_at || null,
      ends_at: item.ends_at || null,
      cta_enabled: Boolean(item.cta_label),
      cta: {
        label: item.cta_label || "",
        type: isExt ? "external" : "internal",
        action: cleanAction,
        url: item.cta_url || ""
      }
    });

    setErrors({});
    setIsModalOpen(true);
  };

  // Reset editor form to blank draft & open modal
  const handleNewAnnouncement = () => {
    setFormData(DEFAULT_ANNOUNCEMENT);
    setErrors({});
    setIsModalOpen(true);
  };

  // Toggle modal open/close
  const handleToggleModal = () => {
    if (isSaving) return;
    setIsModalOpen((prev) => !prev);
  };

  // ─── Set Announcement Active Status (PUT /api/admin/brand-announcements/{id}) ───
  const handleSetActiveStatus = (item, targetStatus) => {
    const newStatus = targetStatus !== undefined ? targetStatus : !item.is_active;
    if (item.is_active === newStatus) return;
    setIsTogglingId(item.id);

    const token = getToken();
    const config = {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      }
    };

    axios
      .put(
        `${API_URL}admin/brand-announcements/${item.id}`,
        { is_active: newStatus },
        config
      )
      .then((res) => {
        showAlert(
          `Announcement #${item.id} is now ${newStatus ? "ACTIVE (Published)" : "INACTIVE"}.`,
          "success"
        );
        fetchAnnouncements(currentPage);

        if (formData.id === item.id) {
          setFormData((prev) => ({ ...prev, is_active: newStatus }));
        }

        if (newStatus) {
          try {
            localStorage.removeItem(`rdlink_announcement_dismissed_${item.id}`);
            localStorage.removeItem("rdlink_last_dismissed_announcement_id");
            localStorage.setItem(
              "rdlink_admin_announcement",
              JSON.stringify({ ...item, is_active: true, updated_at: new Date().toISOString() })
            );
            localStorage.setItem("rdlink_admin_announcement_timestamp", Date.now().toString());
          } catch (e) { }
        }
      })
      .catch((err) => {
        console.error("Failed to update status:", err);
        showAlert("Could not update announcement status. Please check your admin session.", "danger");
      })
      .finally(() => {
        setIsTogglingId(null);
      });
  };

  const handleToggleActiveStatus = (item) => {
    handleSetActiveStatus(item, !item.is_active);
  };

  // ─── Delete Handlers (matching manage-brands modal) ───
  const handleDeleteAnnouncement = (item) => {
    setDeleteId(item.id);
    setDeleteModal(true);
  };

  const confirmDeleteAnnouncement = () => {
    if (!deleteId) return;
    const targetId = deleteId;
    setDeleteModal(false);
    setIsLoadingList(true);

    const token = getToken();
    axios
      .delete(`${API_URL}admin/brand-announcements/${targetId}`, {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`
        }
      })
      .then(() => {
        showAlert("Announcement deleted successfully.", "success");
        if (formData.id === targetId) {
          setFormData(DEFAULT_ANNOUNCEMENT);
        }
        fetchAnnouncements(currentPage);
      })
      .catch((err) => {
        console.error("Delete failed:", err);
        showAlert("Failed to delete announcement. Please verify your permissions.", "danger");
        setIsLoadingList(false);
      })
      .finally(() => {
        setDeleteId(null);
      });
  };

  // ─── Validation (Backend Section 6.6 Rules) ───
  const validate = () => {
    const errs = validateAnnouncementForm(formData);
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ─── Save Announcement (POST / PUT API) ───
  const handleSave = () => {
    if (!validate()) return;
    setIsSaving(true);

    const hasCta = Boolean(formData.cta_enabled && formData.cta?.label?.trim());
    const isExternal = hasCta && formData.cta?.type === "external";
    const ctaLabel = hasCta ? formData.cta.label.trim() : null;
    let ctaAction = null;
    let ctaUrl = null;

    if (hasCta) {
      if (isExternal) {
        ctaAction = "external_url";
        ctaUrl = formData.cta?.url?.trim() || null;
      } else {
        const action = formData.cta?.action;
        ctaAction = VALID_INTERNAL_ACTIONS.some((a) => a.value === action)
          ? action
          : "create_post";
        ctaUrl = null;
      }
    }

    const titleVal = formData.title?.trim() || "Announcement";
    const bodyVal = formData.body?.trim() || "";
    const startsAt = cleanIsoDate(formData.starts_at);
    const endsAt = cleanIsoDate(formData.ends_at);

    const serverPayload = {
      title: titleVal,
      body: bodyVal,
      cta_label: ctaLabel,
      cta_action: ctaAction,
      cta_url: ctaUrl,
      is_active: Boolean(formData.is_active),
      starts_at: startsAt,
      ends_at: endsAt
    };

    const token = getToken();
    const config = {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      }
    };

    const onSaveSuccess = (saved) => {
      const savedId = saved?.id || formData.id;
      if (savedId) {
        setFormData((prev) => ({ ...prev, id: savedId }));
        const localPayload = {
          ...serverPayload,
          ...(saved || {}),
          id: savedId,
          is_active: Boolean(serverPayload.is_active),
          updated_at: new Date().toISOString()
        };
        localStorage.setItem("rdlink_admin_announcement", JSON.stringify(localPayload));
        try {
          localStorage.removeItem(`rdlink_announcement_dismissed_${savedId}`);
          localStorage.removeItem("rdlink_last_dismissed_announcement_id");
          localStorage.setItem("rdlink_admin_announcement_timestamp", Date.now().toString());
        } catch (e) { }
      }

      successAlert(
        `<p class='font-weight-bold mb-0'>Announcement Saved Successfully!</p><p class='font-small-3'>Saved in database and broadcasted to Brand dashboards.</p>`
      );
      setIsModalOpen(false);
      fetchAnnouncements(currentPage);
    };

    const isUpdate = Boolean(formData.id);
    const requestPromise = isUpdate
      ? axios.put(`${API_URL}admin/brand-announcements/${formData.id}`, serverPayload, config)
      : axios.post(`${API_URL}admin/brand-announcements`, serverPayload, config);

    requestPromise
      .then((res) => {
        const saved = res?.data?.data || res?.data;
        onSaveSuccess(saved);
      })
      .catch((err) => {
        console.error("Backend announcement API call failed:", err);
        let errorDetails = null;
        if (err?.response?.data?.errors && typeof err.response.data.errors === "object") {
          errorDetails = Object.values(err.response.data.errors)
            .flat()
            .join("<br/>");
        }
        const errorMsg =
          errorDetails ||
          err?.response?.data?.message ||
          err?.response?.data?.msg ||
          "Failed to store announcement. Please verify your fields and admin session.";

        errorAlert(
          `<p class='font-weight-bold mb-50'>Save Failed</p><p class='font-small-3 mb-0'>${errorMsg}</p>`
        );
      })
      .finally(() => {
        setIsSaving(false);
      });
  };


  // ─── Filter & Search Handlers (apply on search click or Enter) ───
  const handleSearch = () => {
    setAppliedTitle(searchTitle.trim());
    setAppliedStatus(searchStatus);
    setCurrentPage(1);
  };

  const handleResetFilter = () => {
    setSearchTitle("");
    setSearchStatus("");
    setAppliedTitle("");
    setAppliedStatus("");
    setCurrentPage(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  // Filter list based on APPLIED filters (triggered by clicking search icon)
  const filteredAnnouncements = announcements.filter((item) => {
    let matchTitle = true;
    let matchStatus = true;
    if (appliedTitle) {
      const q = appliedTitle.toLowerCase();
      matchTitle =
        Boolean(item.title && item.title.toLowerCase().includes(q)) ||
        Boolean(item.body && item.body.toLowerCase().includes(q));
    }
    if (appliedStatus) {
      matchStatus =
        appliedStatus === "active" ? Boolean(item.is_active) : !item.is_active;
    }
    return matchTitle && matchStatus;
  });

  // ─── Table Pagination Calculations ───
  const {
    items: displayedAnnouncements,
    currentPage: safeCurrentPage,
    totalPages,
    total: totalCount,
    startIndex,
    endIndex
  } = paginateList(filteredAnnouncements, currentPage, pageSize);

  // If list count shrinks such that currentPage exceeds totalPages, clamp back to totalPages
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      const clamped = Math.max(1, totalPages);
      setCurrentPage(clamped);
      syncPaginationToStorageAndUrl(clamped, pageSize);
    }
  }, [totalPages, currentPage, pageSize]);

  const dataToRender = () => {
    return displayedAnnouncements;
  };

  // ─── DataTable Columns (matching manage-brands style) ───
  const announcementColumns = [
    {
      name: "#ID",
      selector: "id",
      sortable: true,
      minWidth: "75px",
      maxWidth: "90px",
      cell: (row) => (
        <span className="font-weight-bold text-primary font-small-3">#{row.id}</span>
      )
    },
    {
      name: "Heading & Content",
      selector: "title",
      sortable: true,
      minWidth: "340px",
      grow: 2,
      wrap: true,
      cell: (row) => (
        <div className="announcement-content-cell py-50 w-100">
          <div
            className="announcement-cell-title font-weight-bolder text-dark mb-50"
            style={{
              fontSize: "0.92rem",
              lineHeight: "1.35",
              color: "#2c2e3e",
              fontWeight: 600
            }}
          >
            {row.title}
          </div>
          <div
            className="announcement-cell-body text-muted"
            style={{
              fontSize: "0.82rem",
              lineHeight: "1.45",
              color: "#6e6b7b",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis"
            }}
            title={row.body}
          >
            {row.body}
          </div>
        </div>
      )
    },
    {
      name: "Status",
      selector: "is_active",
      sortable: true,
      minWidth: "125px",
      maxWidth: "145px",
      cell: (row) => {
        return (
          <div className="d-flex align-items-center">
            <CustomInput
              className="custom-control-success mr-50"
              type="switch"
              id={`status_switch_${row.id}`}
              name={`status_switch_${row.id}`}
              inline
              checked={Boolean(row.is_active)}
              disabled={isTogglingId === row.id}
              onChange={() => handleToggleActiveStatus(row)}
            />
            {isTogglingId === row.id ? (
              <Spinner size="sm" />
            ) : (
              <span className="font-small-2 font-weight-bold">
                {row.is_active ? (
                  <span className="text-success">Active</span>
                ) : (
                  <span className="text-muted">Inactive</span>
                )}
              </span>
            )}
          </div>
        );
      }
    },
    {
      name: "Schedule Window",
      selector: "starts_at",
      sortable: true,
      minWidth: "180px",
      cell: (row) => (
        <div className="py-25">
          <div>{getScheduleBadge(row)}</div>
          <div className="font-small-2 text-muted mt-25">
            {row.starts_at || row.ends_at ? (
              <>
                <span>{formatDateDisplay(row.starts_at)}</span> &rarr;{" "}
                <span>{formatDateDisplay(row.ends_at)}</span>
              </>
            ) : (
              <span>No expiry limit</span>
            )}
          </div>
        </div>
      )
    },
    {
      name: "Call To Action",
      selector: "cta_label",
      sortable: false,
      minWidth: "190px",
      cell: (row) =>
        row.cta_label ? (
          <div className="d-flex align-items-center flex-wrap py-25">
            <Badge color="light-info" className="mr-50 font-small-2">
              {row.cta_action === "external_url" ? "External" : row.cta_action}
            </Badge>
            <span className="font-weight-bold font-small-3">{row.cta_label}</span>
            {row.cta_url && (
              <a
                href={row.cta_url}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-50 text-primary"
                title={row.cta_url}
              >
                <ExternalLink size={13} />
              </a>
            )}
          </div>
        ) : (
          <span className="text-muted font-italic font-small-2">None</span>
        )
    },
    {
      name: "Actions",
      allowOverflow: true,
      minWidth: "85px",
      maxWidth: "100px",
      cell: (row) => {
        const isRowActive = Boolean(row.is_active);
        return (
          <div className="d-flex pl-1">
            <UncontrolledDropdown>
              <DropdownToggle className="pr-1 cursor-pointer" tag="span">
                <MoreHorizontal size={15} />
              </DropdownToggle>
              <DropdownMenu right>
                <DropdownItem
                  className="w-100 d-flex align-items-center"
                  onClick={() => handleEditAnnouncement(row)}
                >
                  <Edit2 size={15} />
                  <span className="align-middle ml-50">Edit</span>
                </DropdownItem>

                <DropdownItem
                  className="w-100 d-flex align-items-center"
                  disabled={isRowActive || isTogglingId === row.id}
                  onClick={() => handleSetActiveStatus(row, true)}
                >
                  <Send size={15} />
                  <span className="align-middle ml-50">Publish</span>
                </DropdownItem>

                <DropdownItem
                  className="w-100 d-flex align-items-center"
                  disabled={!isRowActive || isTogglingId === row.id}
                  onClick={() => handleSetActiveStatus(row, false)}
                >
                  <XCircle size={15} />
                  <span className="align-middle ml-50">Make Inactive</span>
                </DropdownItem>

                <DropdownItem divider />

                <DropdownItem
                  className="w-100 d-flex align-items-center text-danger"
                  onClick={() => handleDeleteAnnouncement(row)}
                >
                  <Trash2 size={15} />
                  <span className="align-middle ml-50">Delete</span>
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
          </div>
        );
      }
    }
  ];

  return (
    <Fragment>
      <Card>
        <CardHeader className="d-flex align-items-center justify-content-between flex-wrap border-bottom p-1 px-2">
          <CardTitle tag="h4">Manage Announcements</CardTitle>
          <div>
            <Button.Ripple
              size="sm"
              color="primary"
              onClick={handleNewAnnouncement}
            >
              <Plus size={15} /> Create Announcement
            </Button.Ripple>
          </div>
        </CardHeader>

        <CardBody>
          <Row form className="mt-1 mb-50">
            <Col lg="4" md="6">
              <FormGroup>
                <Label for="search-title">Search Announcement</Label>
                <Input
                  id="search-title"
                  placeholder="Search by title or content..."
                  value={searchTitle}
                  onChange={(e) => setSearchTitle(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </FormGroup>
            </Col>
            <Col lg="3" md="4">
              <FormGroup>
                <Label for="search-status">Status:</Label>
                <Input
                  type="select"
                  id="search-status"
                  value={searchStatus}
                  onChange={(e) => setSearchStatus(e.target.value)}
                >
                  <option value="">---Select---</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </Input>
              </FormGroup>
            </Col>
            <Col lg="2" md="2">
              <FormGroup>
                <Label>Action:</Label>
                <div className="d-flex align-items-center">
                  <Button.Ripple
                    className="btn-icon mr-50"
                    color="primary"
                    onClick={handleSearch}
                    title="Search"
                  >
                    <Search size={16} />
                  </Button.Ripple>
                  {isFilterActive && (
                    <Button.Ripple
                      className="btn-icon"
                      outline
                      color="primary"
                      onClick={handleResetFilter}
                      title="Reset Filter"
                    >
                      <RefreshCcw size={16} />
                    </Button.Ripple>
                  )}
                </div>
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col>
              <Alert
                color={alertType || "success"}
                isOpen={visibleAlert}
                toggle={() => setVisibleAlert(false)}
              >
                <div className="alert-body">{alertMsg}</div>
              </Alert>
            </Col>
          </Row>
        </CardBody>

        {isLoadingList ? (
          <div className="w-100 my-4 text-center">
            <Spinner color="primary" />
          </div>
        ) : (
          <div className="announcements-management-table">
            <DataTable
              noHeader={true}
              columns={announcementColumns}
              subHeader={false}
              className="react-dataTable"
              data={dataToRender()}
              style={{ minHeight: "450px" }}
              noDataComponent={
                <div className="text-center py-4 px-2">
                  <AlertCircle size={36} className="text-muted mb-1" />
                  <h6 className="text-dark font-weight-bold">No Announcements Found</h6>
                  <p className="text-muted font-small-3 mb-2">
                    {isFilterActive
                      ? "No announcements match your filter criteria."
                      : "Click \"Create Announcement\" to publish your first announcement to Brand dashboards."}
                  </p>
                  {isFilterActive ? (
                    <Button color="primary" size="sm" outline onClick={handleResetFilter}>
                      <RefreshCcw size={14} className="mr-50" />
                      Clear Filters
                    </Button>
                  ) : (
                    <Button color="primary" size="sm" onClick={handleNewAnnouncement}>
                      <Plus size={14} className="mr-50" />
                      Create Announcement
                    </Button>
                  )}
                </div>
              }
            />
            <CustomPagination
              pageLinks={generatePaginationLinks(safeCurrentPage, totalPages)}
              onPaginationClick={handleCustomPagination}
            />
          </div>
        )}

        {/* ─── Create / Edit Modal ─── */}
        <Modal
          isOpen={isModalOpen}
          toggle={handleToggleModal}
          className="modal-dialog-centered"
          size="lg"
          backdrop="static"
          keyboard={!isSaving}
        >
          <ModalHeader toggle={handleToggleModal}>
            {formData.id ? `Edit Announcement #${formData.id}` : "Create New Announcement"}
          </ModalHeader>

          <Form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
            <ModalBody>
              {/* Live Preview Inside Modal */}
              <div className="announcement-modal-preview mb-2">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="font-small-2 font-weight-bold text-uppercase text-secondary d-flex align-items-center">
                    <Eye size={13} className="mr-50 text-primary" /> Live Preview
                  </span>
                  <Badge color={formData.is_active ? "light-success" : "light-secondary"}>
                    {formData.is_active ? "Active" : "Inactive"}
                  </Badge>
                </div>

                <div className="dashboard-guidance-wrapper mb-0 w-100">
                  <div className="figma-admin-banner mb-0" id="preview-admin-announcement-modal">
                    <div className="banner-left-avatar-circle" />

                    <div className="banner-center-content">
                      <div className="banner-meta-row">
                        <span className="banner-source-tag">FROM THE RD LINK</span>
                        <span className="banner-admin-pill">Admin</span>
                      </div>

                      {formData.title ? (
                        <h5 className="banner-title text-white font-weight-bold">
                          {formData.title}
                        </h5>
                      ) : null}

                      <p className="banner-body-text" style={{ whiteSpace: "pre-line" }}>
                        {formData.body || "Your announcement message will appear here in plain text..."}
                      </p>

                      {formData.cta_enabled && formData.cta?.label ? (
                        <div className="banner-cta-container">
                          <button
                            type="button"
                            className="btn btn-sm btn-primary banner-cta-btn"
                          >
                            <span>{formData.cta.label}</span>
                            <ArrowRight size={13} className="ml-50" />
                          </button>
                        </div>
                      ) : null}
                    </div>

                    <div className="banner-right-action">
                      <span className="banner-dismiss-link text-white-50">
                        Dismiss
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Form Row 1: Heading / Title (8 cols) & Status (4 cols) */}
              <Row>
                <Col md="8" xs="12">
                  <FormGroup>
                    <Label className="form-label font-weight-bold" for="announcement-title">
                      Heading / Title <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="text"
                      id="announcement-title"
                      placeholder="e.g. Summer is peak RD engagement season"
                      maxLength={191}
                      value={formData.title}
                      className={errors.title ? "is-invalid" : ""}
                      onChange={(e) => {
                        setFormData({ ...formData, title: e.target.value });
                        if (errors.title) setErrors({ ...errors, title: null });
                      }}
                    />
                    <div className="d-flex justify-content-between align-items-center mt-25">
                      {errors.title ? (
                        <div className="validation-err">{errors.title}</div>
                      ) : (
                        <small className="text-muted">Brief, compelling headline shown in bold.</small>
                      )}
                      <small className="text-muted ml-auto">{formData.title.length}/191</small>
                    </div>
                  </FormGroup>
                </Col>

                <Col md="4" xs="12">
                  <FormGroup>
                    <Label className="form-label font-weight-bold" for="announcement-active-switch">
                      Status
                    </Label>
                    <div className="pt-50">
                      <CustomInput
                        type="switch"
                        id="announcement-active-switch"
                        name="is_active"
                        className="custom-control-success"
                        label={formData.is_active ? "Active" : "Inactive"}
                        checked={Boolean(formData.is_active)}
                        onChange={(e) =>
                          setFormData({ ...formData, is_active: e.target.checked })
                        }
                        inline
                      />
                      <small className="text-muted d-block mt-25">
                        Broadcasts to Brand Dashboards
                      </small>
                    </div>
                  </FormGroup>
                </Col>
              </Row>

              {/* Form Row 2: Message Body */}
              <Row>
                <Col xs="12">
                  <FormGroup>
                    <div className="d-flex justify-content-between align-items-center mb-25">
                      <Label className="form-label font-weight-bold mb-0" for="announcement-body">
                        Message Body <span className="text-danger">*</span>
                      </Label>
                      <small className="text-muted">{formData.body.length}/5000</small>
                    </div>
                    <Input
                      type="textarea"
                      rows="4"
                      id="announcement-body"
                      placeholder="Enter announcement copy here..."
                      maxLength={5000}
                      value={formData.body}
                      className={errors.body ? "is-invalid" : ""}
                      onChange={(e) => {
                        setFormData({ ...formData, body: e.target.value });
                        if (errors.body) setErrors({ ...errors, body: null });
                      }}
                    />
                    {errors.body && <div className="validation-err">{errors.body}</div>}
                  </FormGroup>
                </Col>
              </Row>

              {/* Form Row 3: Scheduling Dates */}
              <Row>
                <Col md="6" xs="12">
                  <FormGroup>
                    <div className="d-flex justify-content-between align-items-center mb-25">
                      <Label className="form-label font-weight-bold mb-0" for="announcement-starts-at">
                        Start Date (Optional)
                      </Label>
                      {formData.starts_at && (
                        <Button
                          color="link"
                          className="p-0 text-danger font-small-2"
                          onClick={() => setFormData({ ...formData, starts_at: null })}
                        >
                          Clear
                        </Button>
                      )}
                    </div>
                    <Flatpickr
                      id="announcement-starts-at"
                      className="form-control"
                      value={formData.starts_at}
                      options={{ dateFormat: "Y-m-d H:i", enableTime: true }}
                      onChange={([date]) =>
                        setFormData({ ...formData, starts_at: date || null })
                      }
                      placeholder="Show immediately..."
                    />
                    <small className="text-muted d-block mt-25">Leave blank to show immediately</small>
                  </FormGroup>
                </Col>

                <Col md="6" xs="12">
                  <FormGroup>
                    <div className="d-flex justify-content-between align-items-center mb-25">
                      <Label className="form-label font-weight-bold mb-0" for="announcement-ends-at">
                        Expiry Date (Optional)
                      </Label>
                      {formData.ends_at && (
                        <Button
                          color="link"
                          className="p-0 text-danger font-small-2"
                          onClick={() => {
                            setFormData({ ...formData, ends_at: null });
                            if (errors.ends_at) setErrors({ ...errors, ends_at: null });
                          }}
                        >
                          Clear
                        </Button>
                      )}
                    </div>
                    <Flatpickr
                      id="announcement-ends-at"
                      className={`form-control ${errors.ends_at ? "is-invalid" : ""}`}
                      value={formData.ends_at}
                      options={{ dateFormat: "Y-m-d H:i", enableTime: true }}
                      onChange={([date]) => {
                        setFormData({ ...formData, ends_at: date || null });
                        if (errors.ends_at) setErrors({ ...errors, ends_at: null });
                      }}
                      placeholder="No expiration..."
                    />
                    {errors.ends_at ? (
                      <div className="validation-err">{errors.ends_at}</div>
                    ) : (
                      <small className="text-muted d-block mt-25">Automatically hides after this time</small>
                    )}
                  </FormGroup>
                </Col>
              </Row>

              {/* Form Row 4: Call to Action (CTA) Card */}
              <Card className="border shadow-none mb-0">
                <CardBody className="p-1">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="mb-25 font-weight-bold">Call-to-Action (CTA) Button</h6>
                      <small className="text-muted">
                        Enable an action button inside the banner
                      </small>
                    </div>
                    <CustomInput
                      type="switch"
                      id="cta-enable-switch"
                      name="cta_enabled"
                      className="custom-control-success"
                      checked={Boolean(formData.cta_enabled)}
                      onChange={(e) =>
                        setFormData({ ...formData, cta_enabled: e.target.checked })
                      }
                      inline
                      label=""
                    />
                  </div>

                  {formData.cta_enabled && (
                    <Row className="mt-1 pt-1 border-top">
                      <Col md="4" xs="12">
                        <FormGroup className="mb-md-0 mb-1">
                          <Label className="form-label font-weight-bold" for="cta-label">
                            Button Label <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="text"
                            id="cta-label"
                            placeholder="e.g. Create Outreach"
                            maxLength={80}
                            value={formData.cta?.label || ""}
                            className={errors.cta_label ? "is-invalid" : ""}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                cta: { ...formData.cta, label: e.target.value }
                              })
                            }
                          />
                          {errors.cta_label && (
                            <div className="validation-err">{errors.cta_label}</div>
                          )}
                        </FormGroup>
                      </Col>

                      <Col md="4" xs="12">
                        <FormGroup className="mb-md-0 mb-1">
                          <Label className="form-label font-weight-bold" for="cta-type">
                            Destination Type
                          </Label>
                          <Input
                            type="select"
                            id="cta-type"
                            value={formData.cta?.type || "internal"}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                cta: { ...formData.cta, type: e.target.value }
                              })
                            }
                          >
                            <option value="internal">Internal App Action</option>
                            <option value="external">External Website (HTTPS URL)</option>
                          </Input>
                        </FormGroup>
                      </Col>

                      <Col md="4" xs="12">
                        {formData.cta?.type === "internal" ? (
                          <FormGroup className="mb-0">
                            <Label className="form-label font-weight-bold" for="cta-action">
                              App Action
                            </Label>
                            <Input
                              type="select"
                              id="cta-action"
                              value={formData.cta?.action || "create_post"}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  cta: { ...formData.cta, action: e.target.value }
                                })
                              }
                            >
                              {VALID_INTERNAL_ACTIONS.map((act) => (
                                <option key={act.value} value={act.value}>
                                  {act.label}
                                </option>
                              ))}
                            </Input>
                          </FormGroup>
                        ) : (
                          <FormGroup className="mb-0">
                            <Label className="form-label font-weight-bold" for="cta-url">
                              External URL <span className="text-danger">*</span>
                            </Label>
                            <Input
                              type="url"
                              id="cta-url"
                              placeholder="https://example.com"
                              maxLength={500}
                              value={formData.cta?.url || ""}
                              className={errors.cta_url ? "is-invalid" : ""}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  cta: { ...formData.cta, url: e.target.value }
                                })
                              }
                            />
                            {errors.cta_url && (
                              <div className="validation-err">{errors.cta_url}</div>
                            )}
                          </FormGroup>
                        )}
                      </Col>
                    </Row>
                  )}
                </CardBody>
              </Card>
            </ModalBody>

            <ModalFooter>
              <Button
                color="secondary"
                outline
                type="button"
                onClick={handleToggleModal}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                type="submit"
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <Spinner size="sm" className="mr-50" />
                    Saving...
                  </>
                ) : (
                  formData.id ? "Update Announcement" : "Create Announcement"
                )}
              </Button>
            </ModalFooter>
          </Form>
        </Modal>

        {/* ─── Delete Confirmation Modal (matching manage-brands) ─── */}
        <Modal
          isOpen={deleteModal}
          toggle={() => setDeleteModal(!deleteModal)}
          className="modal-dialog-centered"
        >
          <ModalHeader toggle={() => setDeleteModal(!deleteModal)}>
            Delete Announcement
          </ModalHeader>
          <ModalBody style={{ minHeight: "50px" }}>
            Are you sure want to delete this announcement?
          </ModalBody>
          <ModalFooter>
            <Button
              color="primary"
              outline
              onClick={() => setDeleteModal(!deleteModal)}
            >
              No
            </Button>
            <Button color="danger" onClick={confirmDeleteAnnouncement}>
              Yes
            </Button>
          </ModalFooter>
        </Modal>
      </Card>
    </Fragment>
  );
};

export default AdminAnnouncements;

